// src/screens/admin/blog/BlogList.jsx

import React, { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Row, Col } from "react-bootstrap";
import {
  FaPlus,
  FaTimes,
  FaSearch,
  FaBold,
  FaItalic,
  FaAlignLeft,
  FaAlignCenter,
  FaAlignRight,
  FaImage,
  FaPaperclip,
  FaUndo,
  FaRedo,
} from "react-icons/fa";
import { FiBookOpen, FiSend, FiEdit3 } from "react-icons/fi";
import { useRef, useEffect } from "react";
import Swal from "sweetalert2";
import AdminLayout from "../../../layouts/AdminLayout";
import ActionDropdown from "../../../components/admin/dropdown/action/ActionDropdown";
import StatCard from "../../../components/admin/statcard/StatCard";
import Pagination from "../../../components/general/pagination/Pagination";
import "../../../assets/css/admin/blog/Blogs.css";
import MyContext from "../../../context/MyContext";
import axios from "axios";
import { BlogsListSkeleton } from "../../../components/general/skeleton/SkeletonBlock";
// ══════════════════════════════════════════════
//  RICH EDITOR (same as before)
// ══════════════════════════════════════════════
const RichEditor = ({ value, onChange }) => {
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const [activeTab, setActiveTab] = useState("format");

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value || "";
    }
  }, [value]);

  const exec = (cmd, val = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
  };

  const handleInput = () => onChange(editorRef.current?.innerHTML || "");

  const handleFileInsert = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const link = document.createElement("a");
    link.href = URL.createObjectURL(file);
    link.target = "_blank";
    link.textContent = file.name;
    editorRef.current?.focus();
    document.execCommand("insertHTML", false, link.outerHTML);
    onChange(editorRef.current?.innerHTML || "");
    e.target.value = "";
  };

  const handleImageInsert = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      exec("insertImage", reader.result);
      onChange(editorRef.current?.innerHTML || "");
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const toolbarButtons = {
    format: [
      { icon: <FaBold />, title: "Bold", action: () => exec("bold") },
      { icon: <FaItalic />, title: "Italic", action: () => exec("italic") },
      { type: "divider" },
      {
        icon: <FaAlignLeft />,
        title: "Align Left",
        action: () => exec("justifyLeft"),
      },
      {
        icon: <FaAlignCenter />,
        title: "Align Center",
        action: () => exec("justifyCenter"),
      },
      {
        icon: <FaAlignRight />,
        title: "Align Right",
        action: () => exec("justifyRight"),
      },
      { type: "divider" },
      { icon: <FaUndo />, title: "Undo", action: () => exec("undo") },
      { icon: <FaRedo />, title: "Redo", action: () => exec("redo") },
    ],
    insert: [
      {
        icon: <FaImage />,
        title: "Insert Image",
        action: () => imageInputRef.current?.click(),
      },
      {
        icon: <FaPaperclip />,
        title: "Attach File",
        action: () => fileInputRef.current?.click(),
      },
    ],
  };

  return (
    <div className="rte-wrapper">
      <div className="rte-tab-bar">
        {["format", "insert"].map((tab) => (
          <button
            key={tab}
            type="button"
            className={`rte-tab ${activeTab === tab ? "rte-tab-active" : ""}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>
      <div className="rte-toolbar">
        {toolbarButtons[activeTab].map((btn, i) =>
          btn.type === "divider" ? (
            <span key={i} className="rte-divider" />
          ) : (
            <button
              key={i}
              type="button"
              title={btn.title}
              className="rte-tool-btn"
              onMouseDown={(e) => {
                e.preventDefault();
                btn.action();
              }}
            >
              {btn.icon}
            </button>
          ),
        )}
      </div>
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        className="rte-editor"
        onInput={handleInput}
        data-placeholder="Write your blog content here..."
      />
      <input
        ref={fileInputRef}
        type="file"
        style={{ display: "none" }}
        onChange={handleFileInsert}
      />
      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleImageInsert}
      />
    </div>
  );
};
const dummyCategories = [
  "Farming Tips",
  "Agri Products",
  "Farming Products",
  "Platform Guide",
  "Market News",
];

const generateSlug = (val) =>
  val
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-");

// ══════════════════════════════════════════════
//  EMPTY FORM STATE
// ══════════════════════════════════════════════
const emptyForm = {
  title: "",
  description: "",
  keywords: "",
  category: "",
  slug: "",
  status: "active",
  featuredImage: null,
};

// ══════════════════════════════════════════════
//  MAIN COMPONENT
// ══════════════════════════════════════════════
const BlogList = () => {
  const navigate = useNavigate();
  const { baseUrl, baseUrlForFiles, authToken } = useContext(MyContext);
  const [loading, setLoading] = useState(true);
  const [image, setImage] = useState(null);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState("active");
  const [slug, setSlug] = useState("");
  const [description, setDescription] = useState("");
  const [keywords, setKeywords] = useState("");
  const [blogsList, setBlogsList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [errorMessage, setErrorMessage] = useState("");
  const [idToUpdateDeleteBlog, setIdToUpdateDeleteBlog] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [currentBlogId, setCurrentBlogId] = useState(null);

  // Form
  const [imagePreview, setImagePreview] = useState(null);

  // ------------------------------
  //   API CALLING
  // ------------------------------
  const addBlog = () => {
    const formData = new FormData();
    formData.append("title", title);
    formData.append("slug", slug);
    formData.append("category", category);
    formData.append("description", description);
    formData.append("status", status);
    formData.append("keywords", keywords);
    if (image) {
      formData.append("image", image);
    }
    axios
      .post(`${baseUrl}/admin/blogs/addBlog`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((response) => {
        console.log(response.data);
        if (response.data.success == true) {
          Swal.fire({
            icon: "success",
            title: "Success",
            text: "Blog added successfully!",
          });
          viewAllBlogs();
        } else {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: "Failed to add blog!",
          });
        }
      })
      .catch((error) => {
        console.log(error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to add blog!",
        });
      });
  };

  const viewAllBlogs = () => {
    setLoading(true);
    axios
      .get(`${baseUrl}/admin/blogs/viewAllBlogs`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        setLoading(false);
        console.log(response.data);
        if (response.data.hasOwnProperty("error")) {
          setErrorMessage(response.data.error);
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.error,
          });
        } else {
          setBlogsList(response.data.blog);
        }
      })
      .catch((error) => {
        setLoading(false);
        console.log(error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Blogs cannot load",
        });
      });
  };

  useEffect(() => {
    if (!authToken) {
      setLoading(false);
      navigate("/admin/login");
      return;
    }
    viewAllBlogs();
  }, [authToken]);

  const updateBlog = () => {
    const formData = new FormData();
    formData.append("id", currentBlogId);
    formData.append("title", title);
    formData.append("slug", slug);
    formData.append("category", category);
    formData.append("description", description);
    formData.append("status", status);
    formData.append("keywords", keywords);
    if (image) {
      formData.append("image", image);
    }
    axios
      .post(`${baseUrl}/admin/blogs/updateBlog`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((response) => {
        console.log(response.data);
        if (response.data.success == true) {
          Swal.fire({
            icon: "success",
            title: "Success",
            text: "Blog updated successfully!",
          });
          viewAllBlogs();
        } else {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: "Failed to update Blog!",
          });
        }
      })
      .catch((error) => {
        console.log(error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to update Blog!",
        });
      });
  };

  const deleteBlog = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This blog will be deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        const formData = new FormData();
        formData.append("id", id);
        axios
          .post(`${baseUrl}/admin/blogs/deleteBlog`, formData, {
            headers: { Authorization: `Bearer ${authToken}` },
          })
          .then((response) => {
            console.log(response.data);
            if (response.data.success == true) {
              Swal.fire({
                icon: "success",
                title: "Deleted!",
                text: "Blog deleted successfully!",
              });
              viewAllBlogs();
            } else {
              Swal.fire({
                icon: "error",
                title: "Error",
                text: "Failed to delete blog!",
              });
            }
          })
          .catch((error) => {
            console.log(error);
            Swal.fire({
              icon: "error",
              title: "Error",
              text: "Failed to delete blog!",
            });
          });
      }
    });
  };

  const toggleBlogStatus = (id, currentStatus) => {
    const formData = new FormData();
    formData.append("id", id);
    formData.append("categorystatus", currentStatus ? 0 : 1);
    axios
      .post(`${baseUrl}/admin/blogs/updateBlog`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((response) => {
        if (response.data.hasOwnProperty("error")) {
          setErrorMessage(response.data.error);
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.error,
          });
        } else {
          viewAllBlogs();
        }
      })
      .catch((error) => {
        console.log(error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to update status.",
        });
      });
  };

  // ── Stats ─────────────────────────────────────────────────────────────────
  const statsData = [
    {
      icon: FiBookOpen,
      iconBg: "linear-gradient(135deg, #e8f5f0, #d4ede3)",
      label: "Total blogsList",
      value: blogsList.length.toString(),
      badge: "All",
    },
    {
      icon: FiSend,
      iconBg: "linear-gradient(135deg, #e8f5f0, #d4ede3)",
      label: "Published",
      value: blogsList.filter((b) => b.status === "active").length.toString(),
      badge: "Live",
    },
    {
      icon: FiEdit3,
      iconBg: "linear-gradient(135deg, #fff3e0, #ffe0b2)",
      label: "Drafts",
      value: blogsList.filter((b) => b.status === "inactive").length.toString(),
      badge: "Draft",
    },
  ];

  // ── Filter ────────────────────────────────────────────────────────────────
  const filteredblogsList = blogsList.filter(
    (b) =>
      (b.title || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (b.category || "").toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // ── Form helpers ──────────────────────────────────────────────────────────
  const stripHtml = (html) => (html ? html.replace(/<[^>]*>/g, "").trim() : "");
  // const validateAndSubmit = (statusOverride) => {
  //   const missing = [];
  //   if (!title) missing.push("Title");
  //   if (!stripHtml(description)) missing.push("Description");
  //   if (statusOverride === "active" && !image && !imagePreview)
  //     missing.push("Featured Image");

  //   if (missing.length) {
  //     Swal.fire({
  //       title: "Missing Required Fields",
  //       html: `<ul style="text-align:left">${missing.map((f) => `<li>${f}</li>`).join("")}</ul>`,
  //       icon: "error",
  //       confirmButtonColor: "#097d43",
  //     });
  //     return;
  //   }

  //   const newData = {
  //     title,
  //     description,
  //     keywords,
  //     category,
  //     slug,
  //     status: statusOverride,
  //   };

  //   if (isEditing) {
  //     setBlogsList((prev) =>
  //       prev.map((b) =>
  //         b.id === currentBlogId
  //           ? { ...b, ...newData, image: imagePreview || b.image }
  //           : b,
  //       ),
  //     );
  //     Swal.fire({
  //       title: "Blog Updated!",
  //       icon: "success",
  //       confirmButtonColor: "#097d43",
  //       timer: 2000,
  //     });
  //   } else {
  //     const newBlog = {
  //       id: Date.now(),
  //       ...newData,
  //       image:
  //         imagePreview ||
  //         "https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=300&h=180&fit=crop",
  //     };
  //     setBlogsList((prev) => [newBlog, ...prev]);
  //     Swal.fire({
  //       title: "Blog Added!",
  //       icon: "success",
  //       confirmButtonColor: "#097d43",
  //       timer: 2000,
  //     });
  //   }
  //   setShowAddModal(false);
  // };

  // ── ActionDropdown actions ────────────────────────────────────────────────
  const getActions = (blog) => {
    const isActive = blog.status === "active";
    return [
      {
        icon: "MdVisibility",
        label: "View Details",
        onClick: () => {
          setSelectedBlog(blog);
          setShowViewModal(true);
        },
      },
      {
        icon: "MdEdit",
        label: "Edit Blog",
        onClick: () => {
          setIsEditing(true);
          setCurrentBlogId(blog.id);
          setTitle(blog.title || "");
          setDescription(blog.description || "");
          setKeywords(blog.keywords || "");
          setCategory(blog.category || "");
          setSlug(blog.slug || "");
          setStatus(blog.status || "active");
          setImage(null);
          // ── FIXED: pehle sirf raw path (e.g. "admin/blog/xyz.jpg")
          //     set ho raha tha, baseUrlForFiles prefix ke bagair —
          //     is se <img src={imagePreview}> broken ho jati thi aur
          //     lagta tha image edit hi nahi ho rahi. Ab View modal
          //     wale "Edit Blog" button jaisa full URL banaya ──
          setImagePreview(
            blog.image ? `${baseUrlForFiles}/${blog.image}` : null,
          );
          setShowAddModal(true);
        },
      },
      {
        icon: isActive ? "MdToggleOff" : "MdToggleOn",
        label: isActive ? "Inactive" : "Activate",
        onClick: () => {
          toggleBlogStatus(blog.id, isActive);
        },
        variant: isActive ? "warning" : "success",
      },
      {
        icon: "MdDelete",
        label: "Delete",
        onClick: () => {
          deleteBlog(blog.id);
        },
      },
    ];
  };

  if (loading) {
    return (
      <AdminLayout>
        <BlogsListSkeleton />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="bl-page">
        {/* ── Page Header ── */}
        <div className="bl-page-header">
          <div>
            <h1 className="ksa-layout-page-title">Blog Management</h1>
            <p className="ksa-layout-page-subtitle">
              Create and manage informative blog posts for KisaanMart users.
            </p>
          </div>
          <button
            className="bl-add-btn"
            onClick={() => {
              setIsEditing(false);
              setCurrentBlogId(null);
              setTitle("");
              setDescription("");
              setKeywords("");
              setCategory("");
              setSlug("");
              setStatus("active");
              setImage(null);
              setImagePreview(null);
              setShowAddModal(true);
            }}
          >
            <FaPlus /> Add New Blog
          </button>
        </div>

        {/* ── Stat Cards ── */}
        <Row className="g-4 mb-4 bl-stats">
          {statsData.map((stat, idx) => (
            <Col key={idx} xs={12} md={4}>
              <StatCard {...stat} />
            </Col>
          ))}
        </Row>

        {/* ── Table Card ── */}
        <div className="ksa-card">
          {/* Search */}
          <div className="bl-table-header">
            <div className="bl-search">
              <FaSearch size={14} />
              <input
                type="text"
                placeholder="Search blogsList..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>

          {/* Table */}
          <div className="bl-table-wrapper">
          <table className="bl-table">
            <thead>
              <tr>
                <th>id</th>
                <th>Image</th>
                <th>Title</th>
                <th>Category</th>
                <th>Description</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredblogsList
                .slice(
                  (currentPage - 1) * itemsPerPage,
                  currentPage * itemsPerPage,
                )
                .map((blog, idx) => {
                  const isActive = blog.status === "active";
                  return (
                    <tr key={blog.id}>
                      <td className="bl-td-num">{blog.id}</td>
                      <td>
                        <img
                          src={`${baseUrlForFiles}/${blog.image}`}
                          alt={blog.title}
                          className="bl-table-img"
                        />
                      </td>
                      <td>
                        <span className="bl-td-title">{blog.title}</span>
                      </td>
                      <td>
                        <span className="bl-td-category">
                          {blog.category || "—"}
                        </span>
                      </td>
                      <td>
                        <span>
                          {stripHtml(blog.description).length > 80
                            ? stripHtml(blog.description).substring(0, 80) +
                              "..."
                            : stripHtml(blog.description) || "—"}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`product-status ${
                            isActive ? "in-stock" : "out-stock"
                          }`}
                        >
                          {isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td>
                        <ActionDropdown actions={getActions(blog)} />
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
          </div>

          {filteredblogsList.length === 0 && (
            <div className="bl-empty">
              <div className="bl-empty-icon">📝</div>
              <h4>No blogsList found</h4>
              <p>Try a different search or add a new blog post.</p>
            </div>
          )}

          <Pagination
            totalItems={filteredblogsList.length}
            itemsPerPage={itemsPerPage}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={setItemsPerPage}
          />
        </div>

        {/* ══════════════════════════════════════════
            ADD / EDIT MODAL
        ══════════════════════════════════════════ */}
        {showAddModal && (
          <div
            className="bl-modal-overlay"
            onClick={() => setShowAddModal(false)}
          >
            <div
              className="bl-modal bl-modal-lg"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="bl-modal-header">
                <h3 className="bl-modal-title">
                  {isEditing ? "Edit Blog Post" : "Add New Blog Post"}
                </h3>
                <button
                  className="bl-modal-close"
                  onClick={() => setShowAddModal(false)}
                >
                  <FaTimes size={16} />
                </button>
              </div>

              {/* Modal Body */}
              <div className="bl-modal-body">
                <Row className="g-4">
                  {/* LEFT: Featured Image */}
                  <Col xs={12} md={4}>
                    <div className="bl-form-group">
                      <label className="bl-form-label">Featured Image *</label>
                      {imagePreview ? (
                        <div className="bl-img-preview-box">
                          <img
                            src={imagePreview}
                            alt="Preview"
                            className="bl-img-preview"
                          />
                          <button
                            className="bl-remove-img"
                            onClick={() =>
                              Swal.fire({
                                title: "Remove Image?",
                                icon: "warning",
                                showCancelButton: true,
                                confirmButtonColor: "#dc3545",
                                confirmButtonText: "Remove",
                                // ── FIXED: ye confirm popup pehle
                                //     modal ke NEECHE chup jata tha
                                //     (z-index clash). didOpen se
                                //     Swal ka container force upar
                                //     laya ja raha hai ──
                                didOpen: () => {
                                  const container = document.querySelector(
                                    ".swal2-container",
                                  );
                                  if (container) container.style.zIndex = 999999;
                                },
                              }).then((r) => {
                                if (r.isConfirmed) {
                                  setImagePreview(null);
                                  setImage(null);
                                  setImagePreview(null);
                                }
                              })
                            }
                          >
                            <FaTimes />
                          </button>
                        </div>
                      ) : (
                        <label
                          htmlFor="bl-img-upload"
                          className="bl-upload-zone"
                        >
                          <div className="bl-upload-inner">
                            <span className="bl-upload-emoji">📷</span>
                            <span className="bl-upload-text">
                              Click to Upload
                            </span>
                            <span className="bl-upload-hint">
                              PNG, JPG up to 5MB
                            </span>
                          </div>
                          <input
                            id="bl-img-upload"
                            type="file"
                            accept="image/*"
                            style={{ display: "none" }}
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (!file) return;
                              if (file.size > 5 * 1024 * 1024) {
                                Swal.fire({
                                  title: "File Too Large",
                                  text: "Max 5MB allowed",
                                  icon: "error",
                                  confirmButtonColor: "#097d43",
                                });
                                return;
                              }
                              if (!file.type.startsWith("image/")) {
                                Swal.fire({
                                  title: "Invalid File",
                                  text: "Select a valid image",
                                  icon: "error",
                                  confirmButtonColor: "#097d43",
                                });
                                return;
                              }
                              setImagePreview(URL.createObjectURL(file));
                              setImage(file);
                            }}
                          />
                        </label>
                      )}
                    </div>
                  </Col>

                  {/* RIGHT: Form fields */}
                  <Col xs={12} md={8}>
                    {/* Title */}
                    <div className="bl-form-group">
                      <label className="bl-form-label">Title *</label>
                      <input
                        type="text"
                        name="title"
                        placeholder="Enter blog title here"
                        className="bl-input"
                        value={title}
                        onChange={(e) => {
                          //                          setTitle(e.target.value);
                          //  setSlug(generateSlug(e.target.value));
                          const value = e.target.value;

                          setTitle(value);
                          setSlug(generateSlug(value));
                        }}
                      />
                    </div>

                    {/* Slug */}
                    <div className="bl-form-group">
                      <label className="bl-form-label">
                        Slug <span className="bl-hint">(auto-generated)</span>
                      </label>
                      <input
                        type="text"
                        className="bl-input bl-readonly"
                        value={slug}
                        readOnly
                      />
                    </div>

                    <Row className="g-3">
                      {/* Category */}
                      <Col xs={12} sm={6}>
                        <div className="bl-form-group">
                          <label className="bl-form-label">Category</label>
                          <select
                            name="category"
                            className="bl-input"
                            value={category}
                            onChange={(e) => {
                              setCategory(e.target.value);
                            }}
                          >
                            <option value="">-- Select Category --</option>
                            {dummyCategories.map((cat, i) => (
                              <option key={i} value={cat}>
                                {cat}
                              </option>
                            ))}
                          </select>
                        </div>
                      </Col>

                      {/* Status */}
                      <Col xs={12} sm={6}>
                        <div className="bl-form-group">
                          <label className="bl-form-label">Status</label>
                          <select
                            name="status"
                            className="bl-input"
                            value={status}
                            onChange={(e) => {
                              setStatus(e.target.value);
                            }}
                          >
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                          </select>
                        </div>
                      </Col>
                    </Row>

                    {/* Keywords */}
                    <div className="bl-form-group">
                      <label className="bl-form-label">
                        Keywords{" "}
                        <span className="bl-hint">(comma separated)</span>
                      </label>
                      <input
                        type="text"
                        name="keywords"
                        placeholder="e.g. wheat, fertilizer, farming"
                        className="bl-input"
                        value={keywords}
                        onChange={(e) => {
                          setKeywords(e.target.value);
                        }}
                      />
                    </div>
                  </Col>

                  {/* Description — full width */}
                  <Col xs={12}>
                    <div className="bl-form-group">
                      <label className="bl-form-label">Description *</label>
                      <RichEditor
                        value={description}
                        onChange={(html) => setDescription(html)}
                      />
                    </div>
                  </Col>
                </Row>
              </div>

              {/* Modal Footer */}
              <div className="bl-modal-footer">
                <button
                  className="bl-cancel-btn"
                  onClick={() => setShowAddModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="bl-draft-btn"
                  onClick={() => {
                    setStatus("inactive");
                    setShowAddModal(false);

                    setTimeout(() => {
                      if (isEditing) {
                        updateBlog();
                      } else {
                        addBlog();
                      }
                    }, 100);
                  }}
                >
                  Save Draft
                </button>
                <button
                  className="bl-publish-btn"
                  onClick={() => {
                    setShowAddModal(false);
                    Swal.fire({
                      title: "Publish Blog?",
                      icon: "question",
                      showCancelButton: true,
                      confirmButtonColor: "#097d43",
                      confirmButtonText: "Yes, Publish!",
                    }).then((r) => {
                      if (r.isConfirmed) {
                        setStatus("active");
                        if (isEditing) {
                          updateBlog();
                        } else {
                          addBlog();
                        }
                      } else {
                        setShowAddModal(true);
                      }
                    });
                  }}
                >
                  <FiSend size={13} /> Publish Post
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════
            VIEW MODAL
        ══════════════════════════════════════════ */}
        {showViewModal && selectedBlog && (
          <div
            className="bl-modal-overlay"
            onClick={() => setShowViewModal(false)}
          >
            <div
              className="bl-modal bl-modal-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bl-modal-header">
                <h3 className="bl-modal-title">Blog Details</h3>
                <button
                  className="bl-modal-close"
                  onClick={() => setShowViewModal(false)}
                >
                  <FaTimes size={16} />
                </button>
              </div>

              <div className="bl-modal-body">
                <img
                  src={`${baseUrlForFiles}/${selectedBlog.image}`}
                  alt={selectedBlog.title}
                  className="bl-view-img"
                />
                <h3 className="bl-view-title">{selectedBlog.title}</h3>
                <div className="bl-view-meta">
                  {selectedBlog.category && (
                    <span className="bl-view-tag">
                      📂 {selectedBlog.category}
                    </span>
                  )}
                  {selectedBlog.keywords && (
                    <span className="bl-view-tag">
                      🏷 {selectedBlog.keywords}
                    </span>
                  )}
                  <span
                    className={`bl-view-tag ${selectedBlog.status === "active" ? "bl-tag-active" : "bl-tag-inactive"}`}
                  >
                    {selectedBlog.status === "active"
                      ? "● Active"
                      : "● Inactive"}
                  </span>
                </div>
                <div className="bl-view-slug">🔗 /{selectedBlog.slug}</div>
                <div
                  className="bl-view-body"
                  dangerouslySetInnerHTML={{ __html: selectedBlog.description }}
                />
              </div>

              <div className="bl-modal-footer">
                <button
                  className="bl-cancel-btn"
                  onClick={() => setShowViewModal(false)}
                >
                  Close
                </button>
                <button
                  className="bl-publish-btn"
                  onClick={() => {
                    setShowViewModal(false);
                    setIsEditing(true);
                    setCurrentBlogId(selectedBlog.id);
                    setTitle(selectedBlog.title || "");
                    setDescription(selectedBlog.description || "");
                    setKeywords(selectedBlog.keywords || "");
                    setCategory(selectedBlog.category || "");
                    setSlug(selectedBlog.slug || "");
                    setStatus(selectedBlog.status || "active");

                    setImage(null);
                    setImagePreview(
                      selectedBlog.image
                        ? `${baseUrlForFiles}/${selectedBlog.image}`
                        : null,
                    );
                    // setImagePreview(selectedBlog.image || null);
                    setShowAddModal(true);
                  }}
                >
                  Edit Blog
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default BlogList;
